/**
 * Replaces every `undefined` value in a parsed row with `null`, so a column
 * whose schema admits `undefined` (`.nullish()`) still lands in memory as an
 * explicit `null`. Spread it inside a schema's `.transform` and override only
 * the fields that need real per-field logic:
 *
 *   .transform((row) => ({
 *     ...nullishToNull(row),
 *     attemptCount: row.attemptCount ?? 0,
 *   }))
 *
 * or, when no field needs anything beyond `?? null`, point-free:
 *
 *   .transform(nullishToNull)
 *
 * The mapped type collapses `T | undefined` to `T | null` per field and keeps
 * every other field's type untouched, so the inferred row type is identical to
 * hand-writing `field: row.field ?? null` for every field — without the
 * double-listing where a forgotten field silently drops data.
 *
 * It runs over the *parsed* row, so it can only rewrite keys the parse
 * produced: a key the synced row never carried is absent here too, and no
 * amount of `?? null` invents it. Tolerating an absent column is `syncedRow`'s
 * job, which runs before validation — see `synced-row.ts`.
 */
type NullishToNull<T> = {
  [K in keyof T]-?: undefined extends T[K]
    ? Exclude<T[K], undefined> | null
    : T[K];
};

export function nullishToNull<T extends Record<string, unknown>>(
  row: T
): NullishToNull<T> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(row)) {
    const value = row[key];
    result[key] = value === undefined ? null : value;
  }
  return result as NullishToNull<T>;
}
